import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const ContactPage = () => {
  const navigate = useNavigate();
  
  // SEO Meta Tags
  useSEO({
    title: 'Contact - Nous Contacter | DJ Nomad',
    description: "Contactez l'équipe DJ Nomad pour toute question, suggestion ou demande d'assistance. Nous répondons rapidement !",
    canonical: 'https://djnomad.app/contact'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simuler l'envoi (en production, appeler une API)
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={Mail}
        title="Contactez-Nous"
        subtitle="Une question ? Un problème ? Une suggestion ? On est là !"
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 flex-1">
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Informations de Contact</h3>
              <div className="space-y-4 text-gray-600">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Support Général</div>
                  <a href="mailto:contact@djnomad.app" className="text-violet-600 hover:underline font-semibold">
                    contact@djnomad.app
                  </a>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Support Technique</div>
                  <a href="mailto:support@djnomad.app" className="text-violet-600 hover:underline font-semibold">
                    support@djnomad.app
                  </a>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Légal & Confidentialité</div>
                  <a href="mailto:legal@djnomad.app" className="text-violet-600 hover:underline font-semibold">
                    legal@djnomad.app
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Temps de Réponse</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous nous engageons à répondre dans les <strong className="text-gray-900">24-48 heures</strong> (jours ouvrables). 
                Pour les urgences techniques, contactez-nous directement par email avec "[URGENT]" dans l'objet.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Avant de nous contacter</h3>
              <p className="text-gray-600 leading-relaxed mb-3">
                Consultez notre <a href="/faq" className="text-violet-600 hover:underline font-semibold">FAQ</a> pour des réponses immédiates aux questions courantes.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Envoyer un Message</h3>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom complet
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none transition-colors"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sujet
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none transition-colors"
                    >
                      <option value="general">Question générale</option>
                      <option value="technical">Problème technique</option>
                      <option value="feature">Suggestion de fonctionnalité</option>
                      <option value="bug">Signaler un bug</option>
                      <option value="partnership">Partenariat</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none transition-colors resize-none"
                      placeholder="Votre message..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-bold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Envoyer le Message</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full mb-6">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Message Envoyé !</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Merci pour votre message. Nous vous répondrons dans les 24-48 heures à l'adresse : <strong className="text-gray-900">{formData.email}</strong>
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-emerald-600 hover:underline font-semibold"
                >
                  Envoyer un autre message
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Alternative Contact Methods */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Autres Moyens de Contact</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="p-4">
              <div className="text-4xl mb-3">💬</div>
              <h4 className="text-gray-900 font-semibold mb-2">Chat en Direct</h4>
              <p className="text-gray-500 text-sm">(Bientôt disponible)</p>
            </div>
            <div className="p-4">
              <div className="text-4xl mb-3">🐥</div>
              <h4 className="text-gray-900 font-semibold mb-2">Twitter</h4>
              <p className="text-gray-500 text-sm">@djnomad (Coming soon)</p>
            </div>
            <div className="p-4">
              <div className="text-4xl mb-3">👾</div>
              <h4 className="text-gray-900 font-semibold mb-2">Discord</h4>
              <p className="text-gray-500 text-sm">Communauté DJ Nomad (Coming soon)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default ContactPage;
